import { useQuery } from "@tanstack/react-query";
import * as React from "react";
import { useQueryClient } from "@tanstack/react-query";
import { DataList, PageHeader, StatusPill, type DataListColumn } from "@ozpanel/kit";
import { Button, ConfirmDialog, Section, StateView, useAuth, useToast, When } from "@ozpanel/ui";

interface Job {
  id: string;
  type: string;
  status: string;
  progress: number;
  current_step?: string;
  node?: string;
  created_at: string;
}

const statusTone = (s: string) =>
  s === "succeeded" || s === "complete" ? "ok" : s === "failed" ? "bad" : s === "running" ? "warn" : "neutral";

const columns: DataListColumn<Job>[] = [
  { key: "type", label: "Type", sortable: true },
  {
    key: "status",
    label: "Status",
    sortable: true,
    render: (j) => <StatusPill tone={statusTone(j.status)}>{j.status}</StatusPill>,
  },
  { key: "progress", label: "Progress", sortable: true, value: (j) => j.progress, render: (j) => `${j.progress}%` },
  { key: "current_step", label: "Step", value: (j) => j.current_step || "", render: (j) => j.current_step || "—" },
  { key: "node", label: "Node", sortable: true, value: (j) => j.node || "", render: (j) => j.node || "—" },
  { key: "created_at", label: "Created", sortable: true, render: (j) => <When value={j.created_at} /> },
];

export function JobsScreen() {
  const { client } = useAuth();
  const qc = useQueryClient();
  const { notify } = useToast();
  const [open, setOpen] = React.useState<string | null>(null);
  const [cancelling, setCancelling] = React.useState<Job | null>(null);

  const jobs = useQuery({
    queryKey: ["jobs"],
    queryFn: () => client.apiFetch<{ items: Job[]; total: number }>("/ozpanel/jobs"),
    // A job list that does not move is indistinguishable from a job that is stuck.
    refetchInterval: 10000,
  });
  const events = useQuery({
    queryKey: ["job-events", open],
    enabled: !!open,
    queryFn: () =>
      client.apiFetch<{ events?: { at?: string; level?: string; message?: string; step?: string }[] | null; logs?: string }>(
        `/ozpanel/jobs/${encodeURIComponent(String(open))}/events`,
      ),
    retry: false,
    refetchInterval: 5000,
  });

  return (
    <div>
      <PageHeader title="Jobs" subtitle={jobs.data ? `${jobs.data.total} recorded` : undefined} />
      <DataList
        items={jobs.data?.items ?? []}
        columns={columns}
        getRowKey={(j) => j.id}
        searchable
        searchPlaceholder="Search jobs…" loading={jobs.isLoading}
        error={jobs.error}
        errorContext="jobs" emptyMessage="No jobs yet."
        rowActions={(j) => (
          <div className="flex justify-end gap-2">
            <Button size="sm" variant="ghost" onClick={() => setOpen(open === j.id ? null : j.id)}>
              {open === j.id ? "Close" : "Steps"}
            </Button>
            {/* Only a running job can be stopped; offering it on a finished one is an action that
                can only fail, and it implies a completed job could be undone. */}
            {j.status === "running" && <Button size="sm" variant="danger" onClick={() => setCancelling(j)}>Stop</Button>}
          </div>
        )} />

      {open && (
        <Section className="mt-6" title={`Steps — ${open}`} actions={<Button size="sm" variant="ghost" onClick={() => setOpen(null)}>Close</Button>}>
          <StateView loading={events.isLoading} error={events.error} errorContext="this job's steps">
            {(events.data?.events?.length ?? 0) > 0 ? (
              <ul className="space-y-1">
                {(events.data?.events ?? []).map((e, i) => (
                  <li key={i} className="flex items-start gap-2 border-b border-line py-1 text-[12.5px]">
                    <span className={e.level === "error" ? "text-crit" : e.level === "warn" ? "text-warn" : "text-muted"}>
                      {e.level ?? "info"}
                    </span>
                    <span className="min-w-0">
                      {e.step && <span className="mr-2 font-mono text-[11.5px]">{e.step}</span>}
                      {e.message}
                    </span>
                    <span className="ml-auto shrink-0 text-[11px] text-muted">{e.at}</span>
                  </li>
                ))}
              </ul>
            ) : events.data?.logs ? (
              <pre className="max-h-96 overflow-auto whitespace-pre-wrap rounded-lg border border-line bg-bg px-3 py-2 font-mono text-[11.5px]">
                {events.data.logs}
              </pre>
            ) : (
              <div className="text-[13px] text-muted">Nothing recorded for this job.</div>
            )}
          </StateView>
        </Section>
      )}

      <ConfirmDialog
        open={!!cancelling}
        onOpenChange={(o) => !o && setCancelling(null)}
        title={`Stop ${cancelling?.type ?? "this job"}?`}
        confirmLabel="Stop it"
        destructive
        body={
          <p>
            It stops at the step it has reached. Steps already done stay done — this is not an undo,
            and a job stopped part-way can leave what it was configuring half-finished. A bootstrap
            or join can be resumed afterwards; most other jobs cannot.
          </p>
        }
        onConfirm={() => {
          const j = cancelling;
          setCancelling(null);
          if (!j) return;
          void client
            .apiFetch(`/ozpanel/jobs/${encodeURIComponent(j.id)}/cancel`, { method: "POST" })
            .then(() => {
              notify({ kind: "success", title: "Job stopped" });
              void qc.invalidateQueries({ queryKey: ["jobs"] });
            })
            .catch((e: unknown) => notify({ kind: "error", title: "Could not stop it", body: e instanceof Error ? e.message : String(e) }));
        }}
      />
    </div>
  );
}
